import { Injectable } from '@angular/core';
import { Constants } from '@hub/common/Constants';
import { eDepartment, eItineraryView, eUserRole } from '@hub/generated/enums';
import { UserDetailDto, UserGridSettingPageSize, UserRoleSystemModuleDto, UserSettingsDto } from '@hub/generated/models';
import { ReferenceController } from '@hub/generated/services';
import { AuthService } from '@hub/services/AuthService';
import { BehaviorSubject } from 'rxjs';
import { AppConfigService } from './AppConfigService';

// Security
@Injectable({ providedIn: 'root' })
export class CurrentUserService {
	// Constructors
	constructor(
		private auth: AuthService,
    public referenceController: ReferenceController,
    private appConfigService: AppConfigService) { }

	// Properties
	public Id: BehaviorSubject<string> = new BehaviorSubject<string>(Constants.GUID_EMPTY);
	public Username: BehaviorSubject<string> = new BehaviorSubject<string>('test@rarecrew.com');
	public FullName: BehaviorSubject<string> = new BehaviorSubject<string>('Mock User');
	public UserAvatar: BehaviorSubject<string> = new BehaviorSubject<string>('assets/images/default.png');

	// Grid Settings
	public MainGridPageSizes: BehaviorSubject<UserGridSettingPageSize[]> = new BehaviorSubject<UserGridSettingPageSize[]>([]);
	public SubGridPageSizes: BehaviorSubject<UserGridSettingPageSize[]> = new BehaviorSubject<UserGridSettingPageSize[]>([]);
	public DefaultMainGridPageSize: BehaviorSubject<number> = new BehaviorSubject<number>(Constants.SUB_GRID_PAGE_SIZE);
	public DefaultSubGridPageSize: BehaviorSubject<number> = new BehaviorSubject<number>(Constants.MAIN_GRID_PAGE_SIZE);

	// Itinerary Settings
	public IsItineraryLeftBarOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public IsItineraryRightBarOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public IsItineraryLeftBarPinned: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public IsItineraryRightBarPinned: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public DefaultItinerary: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
	public DefaultItineraryView: BehaviorSubject<eItineraryView | null> = new BehaviorSubject<eItineraryView | null>(null);

	// Security
	public Departments: BehaviorSubject<eDepartment[]> = new BehaviorSubject<eDepartment[]>([]);
	public SecurityRoles: BehaviorSubject<eUserRole[]> = new BehaviorSubject<eUserRole[]>([]);
	public UserSystemModules = new BehaviorSubject<UserRoleSystemModuleDto[]>([]);

	// Enums
	// public enums: BehaviorSubject<EnumTypeDto[]> = new BehaviorSubject<EnumTypeDto[]>([]);

	// Private Fields
	private _currentUser: BehaviorSubject<UserDetailDto> = new BehaviorSubject<UserDetailDto>(new UserDetailDto());

	public get GetTimeZone(): string {
		if (this.auth.isLogged) {
			const timeZoneOffset: string = !!this.UserSettings
				? this.UserSettings.TimeZone == null ? Constants.DEFAULT_TIMEZONE : this.UserSettings.TimeZone.JsName
				: '';

			return timeZoneOffset.replace(':', '');
		}
		return '';
	}

	public get GetUserTimeZone(): string {
		return new Date().toString().match(/\((.*)\)/).pop();
	}
	// public get Get
	public get UserSettings(): UserSettingsDto {
		return this._currentUser.getValue().Settings;
	}

	// Public Methods
	public updateSettings(settings: UserSettingsDto): void {
		let user = this._currentUser.getValue();
		user.Settings = settings;
		this._currentUser.next(user);
		this.loadUserSettings(user)
	}

	// Private Methods
	public load(user: UserDetailDto): void {
		this._currentUser.next(user);
		this.Id.next(user.Id);
		this.Username.next(user.UserName);
		this.FullName.next(user.FullName);
    this.loadUserSettings(user);
    this.appConfigService.loadPrivateAppConfig();

		// Security
		this.Departments.next(user.Departments);
		this.SecurityRoles.next(user.SecurityRoles);
		this.UserSystemModules.next(user.UserRoleSystemModules)
	}

	private loadUserSettings(user: UserDetailDto) {
		this.IsItineraryLeftBarOpen.next(user.Settings.Itinerary.IsItineraryLeftBarOpen);
		this.IsItineraryRightBarOpen.next(user.Settings.Itinerary.IsItineraryRightBarOpen);
		this.IsItineraryLeftBarPinned.next(user.Settings.Itinerary.IsItineraryLeftBarPinned);
		this.IsItineraryRightBarPinned.next(user.Settings.Itinerary.IsItineraryRightBarPinned);
		this.DefaultItinerary.next(!user.Settings.Itinerary.DefaultItinerary
			|| user.Settings.Itinerary.DefaultItinerary == Constants.GUID_EMPTY
			? null : user.Settings.Itinerary.DefaultItinerary);
		this.DefaultItineraryView.next(user.Settings.Itinerary.DefaultItineraryView);
		this.MainGridPageSizes.next(user.Settings.Grid.MainGridPageSizes);
		this.SubGridPageSizes.next(user.Settings.Grid.SubGridPageSizes);
		this.DefaultMainGridPageSize.next(user.Settings.Grid.DefaultSubGridPageSize);
		this.DefaultSubGridPageSize.next(user.Settings.Grid.DefaultSubGridPageSize);
	}
}
